import React, { useEffect, useState } from 'react';
import './App.css';
import Loader from './components/Loader';
import Header from './components/Header';
import About from './components/About';
import Stats from './components/Stats';
import Experience from './components/Experience';
import Projects from './components/Projects';
import Contact from './components/Contact';
import Footer from './components/Footer';

const getInitialTheme = () => {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem('theme');
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

function App() {
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [theme, setTheme] = useState(getInitialTheme);

  // Apply and persist theme.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    window.localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Lock scroll until the app is revealed (loader wipe has begun).
  useEffect(() => {
    document.body.style.overflow = revealed ? '' : 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [revealed]);

  useEffect(() => {
    const syncScrollState = () => {
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollableHeight > 0 ? Math.min(100, (window.scrollY / scrollableHeight) * 100) : 0;
      setScrollProgress(progress);

      const sections = ['home', 'experience', 'projects', 'contact'];
      let currentSection = sections[0];

      sections.forEach((id) => {
        const element = document.getElementById(id);
        if (element && (window.scrollY + window.innerHeight / 2) >= element.offsetTop) {
          currentSection = id;
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', syncScrollState, { passive: true });
    window.addEventListener('resize', syncScrollState);
    syncScrollState();

    return () => {
      window.removeEventListener('scroll', syncScrollState);
      window.removeEventListener('resize', syncScrollState);
    };
  }, []);

  return (
    <>
      {loading && (
        <Loader
          onReveal={() => setRevealed(true)}
          onComplete={() => setLoading(false)}
        />
      )}

      <a href="#main-content" className="skip-link">Skip to content</a>

      <Header
        activeSection={activeSection}
        scrollProgress={scrollProgress}
        theme={theme}
        onToggleTheme={toggleTheme}
      />

      <main id="main-content">
        <div className={`App ${revealed ? 'App--revealed' : 'App--hidden'}`}>
          <About />
          <Stats />
          <Experience />
          <Projects />
          <Contact />
        </div>
      </main>

      <Footer />
    </>
  );
}

export default App;
